import { useState, useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Box } from '@material-ui/core';

type Props = FieldExtensionComponentProps<string>;

/**
 * LoggedUser field extension that fetches the current logged-in user's information
 * from the catalog and auto-populates the field value.
 * Fields are hidden and required, blocking form submission until user info is loaded.
 */
export const LoggedUser = (props: Props) => {
  const { onChange, schema, formData } = props;
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [_loading, setLoading] = useState(true);
  const [_error, setError] = useState<string | null>(null);

  // Determine if this is the name or email field based on schema title/description
  const isEmailField =
    schema.title?.toLowerCase().includes('email') ||
    schema.description?.toLowerCase().includes('email') ||
    schema.title?.toLowerCase().includes('author email');

  useEffect(() => {
    let cancelled = false;

    const fetchUserInfo = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current user identity
        const identity = await identityApi.getBackstageIdentity();
        const userEntityRef = identity.userEntityRef;

        if (!userEntityRef) {
          throw new Error('Unable to determine current user');
        }

        // Fetch user entity from catalog
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);

        if (!userEntity || !userEntity.spec) {
          throw new Error('User entity not found in catalog');
        }

        const spec = userEntity.spec as { profile?: { email?: string; displayName?: string } };
        const email = spec.profile?.email || '';
        const displayName =
          spec.profile?.displayName || userEntity.metadata?.name || email.split('@')[0] || '';

        if (cancelled) return;

        if (!email && !displayName) {
          throw new Error('User information not found in catalog');
        }

        // Output the appropriate value based on field type
        const outputValue = isEmailField ? email : displayName;
        onChange(outputValue);
      } catch (err) {
        if (!cancelled) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to fetch user information';
          setError(errorMessage);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    // Always fetch user info to populate the field
    fetchUserInfo();

    return () => {
      cancelled = true;
    };
  }, [identityApi, catalogApi, isEmailField, onChange, formData]);

  // Render nothing visible - fields are completely hidden with no space taken
  // The form will be blocked from submission until values are populated (via required validation)
  return <Box style={{ display: 'none' }} />;
};
